import { useState, useRef, useCallback, useEffect } from 'react';
import { synthesizeSpeechStream } from '../services/api';

/**
 * 语音播放自定义 Hook
 * 封装了流式语音合成的逻辑，可在多个组件中复用
 */
export const useAudioPlayer = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  
  const audioContextRef = useRef(null);
  const audioBufferQueueRef = useRef([]);
  const isPlayingRef = useRef(false);
  const isPausedRef = useRef(false);
  const nextStartTimeRef = useRef(0);
  const abortControllerRef = useRef(null);

  // 清理音频资源
  const cleanupAudio = useCallback(() => {
    isPlayingRef.current = false;
    isPausedRef.current = false;
    
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    
    audioBufferQueueRef.current = [];
    nextStartTimeRef.current = 0;
    setIsPlaying(false);
    setIsPaused(false);
  }, []);

  // base64 转 Uint8Array
  const base64ToUint8Array = (base64) => {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  };

  // 使用 Web Audio API 播放 PCM 数据
  const playPCMBuffer = (audioContext, dataChunks, sampleRate) => {
    if (!audioContext || audioContext.state === 'closed') return;

    // 合并所有数据块
    const totalLength = dataChunks.reduce((sum, chunk) => sum + chunk.length, 0);
    const combinedData = new Uint8Array(totalLength);
    let offset = 0;
    for (const chunk of dataChunks) {
      combinedData.set(chunk, offset);
      offset += chunk.length;
    }

    // PCM 是 16 位有符号整数，需要转换为 Float32
    const int16Array = new Int16Array(combinedData.buffer);
    const float32Array = new Float32Array(int16Array.length);
    
    // 转换为 -1.0 到 1.0 的浮点数
    for (let i = 0; i < int16Array.length; i++) {
      float32Array[i] = int16Array[i] / 32768.0;
    }

    // 创建音频缓冲区
    const audioBuffer = audioContext.createBuffer(1, float32Array.length, sampleRate);
    audioBuffer.copyToChannel(float32Array, 0);

    // 创建音频源
    const source = audioContext.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(audioContext.destination);
    
    // 计算播放时间，确保连续播放
    const currentTime = audioContext.currentTime;
    const scheduledTime = nextStartTimeRef.current > 0 
      ? Math.max(currentTime, nextStartTimeRef.current)
      : currentTime;
    
    source.start(scheduledTime);
    
    // 更新下一个音频块的开始时间
    nextStartTimeRef.current = scheduledTime + audioBuffer.duration;
    
    // 记录这个缓冲区的结束时间
    audioBufferQueueRef.current.push({
      endTime: scheduledTime + audioBuffer.duration,
      source: source
    });
    
    // 清理已播放的缓冲区记录
    audioBufferQueueRef.current = audioBufferQueueRef.current.filter(
      item => item.endTime > currentTime
    );
  };

  // 播放/暂停/继续语音（使用 Web Audio API 流式播放）
  const playAudio = useCallback(async (text, options = {}) => {
    if (!text) return;

    // 如果正在播放且未暂停，则暂停
    if (isPlayingRef.current && !isPausedRef.current) {
      if (audioContextRef.current && audioContextRef.current.state === 'running') {
        audioContextRef.current.suspend();
        setIsPaused(true);
        isPausedRef.current = true;
      }
      return;
    }

    // 如果已暂停，则继续播放
    if (isPlayingRef.current && isPausedRef.current) {
      if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume();
        setIsPaused(false);
        isPausedRef.current = false;
      }
      return;
    }

    // 开始新的播放
    try {
      setIsPlaying(true);
      setIsPaused(false);
      isPlayingRef.current = true;
      isPausedRef.current = false;
      audioBufferQueueRef.current = [];
      nextStartTimeRef.current = 0;

      // 创建 AudioContext
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;

      // 使用流式 API 获取数据
      const { streamPromise, abortController } = synthesizeSpeechStream(text, {
        voice: options.voice || 'Cherry',
        languageType: options.languageType || 'Chinese',
      });
      
      abortControllerRef.current = abortController;

      const response = await streamPromise;
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let pcmDataBuffer = [];
      let sseBuffer = '';
      let incompleteJsonBuffer = '';
      let hasStartedPlaying = false;
      
      // 添加JSON完整性检查函数
      const isValidJSON = (str) => {
        try {
          JSON.parse(str);
          return true;
        } catch (e) {
          return false;
        }
      };

      // 设置超时检查
      const startTime = Date.now();
      const timeoutCheck = setInterval(() => {
        const elapsed = Date.now() - startTime;
        if (elapsed > 300000) { // 5分钟
          abortController.abort();
          clearInterval(timeoutCheck);
        }
      }, 10000);

      // 读取流数据
      while (true) {
        // 检查是否已暂停，如果暂停则等待
        while (isPausedRef.current && isPlayingRef.current) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }

        if (!isPlayingRef.current) {
          clearInterval(timeoutCheck);
          break;
        }

        const { done, value } = await reader.read();
        
        if (done) {
          clearInterval(timeoutCheck);
          break;
        }

        // 解码数据
        const chunk = decoder.decode(value, { stream: true });
        sseBuffer += chunk;

        // 处理 SSE 格式的数据行
        let lineEndIndex;
        while ((lineEndIndex = sseBuffer.indexOf('\n')) !== -1) {
          const line = sseBuffer.substring(0, lineEndIndex).trim();
          sseBuffer = sseBuffer.substring(lineEndIndex + 1);

          // 跳过 SSE 标准字段（id, event, retry 等），只处理 data 字段
          if (line.startsWith('id:') || line.startsWith('event:') || line.startsWith('retry:')) {
            continue;
          }
          
          if (line.startsWith('data:')) {
            let jsonStr = line.substring(5).trim();
            
            // 跳过结束标记
            if (jsonStr === '[DONE]') {
              continue;
            }
            
            // 只处理非空且看起来像JSON的字符串
            if (jsonStr && jsonStr.length > 0) {
              // 如果有之前未完成的 JSON，先拼接
              if (incompleteJsonBuffer) {
                jsonStr = incompleteJsonBuffer + jsonStr;
                incompleteJsonBuffer = '';
              }
              
              // 基本的JSON格式检查 - 确保以 { 开头
              if (!jsonStr.trim().startsWith('{')) {
                continue;
              }
              
              // 验证JSON完整性
              if (!isValidJSON(jsonStr)) {
                // 检查是否可能是不完整的 JSON
                const trimmed = jsonStr.trim();
                const openBraces = (trimmed.match(/\{/g) || []).length;
                const closeBraces = (trimmed.match(/\}/g) || []).length;
                const openBrackets = (trimmed.match(/\[/g) || []).length;
                const closeBrackets = (trimmed.match(/\]/g) || []).length;
                
                // 如果括号不匹配，可能是未完成的 JSON，保存到缓冲区
                if (openBraces > closeBraces || openBrackets > closeBrackets || 
                    (!trimmed.endsWith('}') && !trimmed.endsWith(']'))) {
                  incompleteJsonBuffer = jsonStr;
                  continue;
                }
                
                console.warn('Invalid JSON (skipping):', jsonStr.substring(0, 100) + '...');
                continue;
              }
              
              try {
                const parsed = JSON.parse(jsonStr);
                
                // 检查错误
                if (parsed.error) {
                  throw new Error(parsed.error);
                }
                
                // 累加音频数据
                if (parsed.output?.audio?.data) {
                  // 解码 base64 PCM 数据
                  const pcmBytes = base64ToUint8Array(parsed.output.audio.data);
                  pcmDataBuffer.push(pcmBytes);
                  
                  // 累积到一定量后开始播放
                  const totalBytes = pcmDataBuffer.reduce((sum, b) => sum + b.length, 0);
                  
                  // 首次累积约 0.3 秒数据就开始播放（减少等待时间）
                  // 24000Hz * 2 bytes * 0.3s = 14400 bytes
                  if (!hasStartedPlaying && totalBytes >= 14400) {
                    hasStartedPlaying = true;

                    // 开始播放累积的数据
                    if (!isPausedRef.current) {
                      playPCMBuffer(audioContext, pcmDataBuffer, 24000);
                    }
                    pcmDataBuffer = [];
                  } else if (hasStartedPlaying && totalBytes >= 8000) {
                    // 之后每累积约 0.16 秒数据就播放
                    if (!isPausedRef.current) {
                      playPCMBuffer(audioContext, pcmDataBuffer, 24000);
                    }
                    pcmDataBuffer = [];
                  }
                }
              } catch (e) {
                console.error('Parse error for JSON:', e);
                console.error('Problematic JSON string:', jsonStr);
              }
            }
          }
        }
      }

      // 处理剩余数据（包括缓冲区中可能的不完整数据）
      if (sseBuffer.trim() || incompleteJsonBuffer) {
        let line = sseBuffer.trim();
        
        // 跳过 SSE 标准字段
        if (line.startsWith('id:') || line.startsWith('event:') || line.startsWith('retry:')) {
          line = '';
        }
        
        // 如果有之前未完成的 JSON，与剩余缓冲区拼接
        if (incompleteJsonBuffer) {
          if (line.startsWith('data:')) {
            line = line.substring(5).trim();
          }
          line = 'data: ' + incompleteJsonBuffer + line;
          incompleteJsonBuffer = '';
        }
        
        if (line.startsWith('data:')) {
          let jsonStr = line.substring(5).trim();
          
          // 跳过结束标记
          if (jsonStr === '[DONE]') {
            // 流已完成
          } else if (jsonStr && jsonStr.length > 0) {
            // 对最终数据也进行完整性检查
            if (jsonStr.trim().startsWith('{') && isValidJSON(jsonStr)) {
              try {
                const parsed = JSON.parse(jsonStr);
                if (parsed.output?.audio?.data) {
                  const pcmBytes = base64ToUint8Array(parsed.output.audio.data);
                  pcmDataBuffer.push(pcmBytes);
                }
              } catch (e) {
                console.error('Parse error for final line:', e);
                console.error('Final JSON string:', jsonStr);
              }
            }
          }
        }
      }

      // 播放剩余数据
      if (pcmDataBuffer.length > 0 && audioContextRef.current && !isPausedRef.current) {
        playPCMBuffer(audioContextRef.current, pcmDataBuffer, 24000);
      }
      
      // 等待所有音频播放完成
      if (audioContextRef.current && nextStartTimeRef.current > 0) {
        const waitForPlaybackComplete = () => {
          if (!isPlayingRef.current || !audioContextRef.current) {
            return;
          }
          
          const currentTime = audioContextRef.current.currentTime;
          const scheduledEndTime = nextStartTimeRef.current;
          const remainingTime = scheduledEndTime - currentTime;
          
          // 如果还有超过100ms的音频未播放，继续等待
          if (remainingTime > 0.1) {
            setTimeout(waitForPlaybackComplete, Math.min(remainingTime * 1000, 100));
          } else {
            // 音频播放完成，清理状态
            setIsPlaying(false);
            setIsPaused(false);
            isPlayingRef.current = false;
            isPausedRef.current = false;
            abortControllerRef.current = null;
            audioContextRef.current?.close().catch(() => {});
            audioContextRef.current = null;
          }
        };
        
        // 开始等待播放完成
        waitForPlaybackComplete();
      } else {
        // 如果没有音频数据或流异常结束，强制清理
        console.warn('Stream ended without audio data or finish mark');
        setTimeout(() => {
          if (isPlayingRef.current) {
            setIsPlaying(false);
            setIsPaused(false);
            isPlayingRef.current = false;
            isPausedRef.current = false;
            abortControllerRef.current = null;
            audioContextRef.current?.close().catch(() => {});
            audioContextRef.current = null;
          }
        }, 1000);
      }
      
    } catch (error) {
      console.error('Error playing audio:', error);
      cleanupAudio();
      if (error.name !== 'AbortError') {
        alert('语音合成失败：' + (error.message || '请检查服务配置'));
      }
    }
  }, [cleanupAudio]);

  // 组件卸载时清理资源
  useEffect(() => {
    return () => {
      cleanupAudio();
    };
  }, [cleanupAudio]);

  return {
    isPlaying,
    isPaused,
    playAudio,
    cleanupAudio,
  };
};

export default useAudioPlayer;
